import React, { useMemo } from 'react';
import { useMarketData } from '../context/MarketContext';
import { useTheme } from '../context/ThemeContext.js';

const HorizontalNormalDistribution = () => {
  const { marketData, marketHistory } = useMarketData();
  const { isLight } = useTheme();
  const accentHex = isLight ? '#5e19e6' : '#00f0ff';
  const gridColor = isLight ? 'rgba(26,26,46,0.1)' : 'white';
  const labelColor = isLight ? 'rgba(26,26,46,0.4)' : 'white';

  const currentPrice = marketData?.price ?? 4291.05;
  const priceChange = marketData?.change ?? 0;

  const distributionData = useMemo(() => {
    if (marketHistory.length === 0) return null;

    const prices = marketHistory.map(h => h.price);
    const minPrice = Math.min(...prices) * 0.995;
    const maxPrice = Math.max(...prices) * 1.005;
    const priceRange = maxPrice - minPrice || 1;

    const mean = prices.reduce((a, b) => a + b, 0) / prices.length;
    const variance = prices.reduce((sum, p) => sum + Math.pow(p - mean, 2), 0) / prices.length;
    const stdDev = Math.sqrt(variance);

    const viewBoxWidth = 1200;
    const viewBoxHeight = 400;
    const distributionHeight = 250;

    const bellPoints = [];
    for (let x = 0; x <= viewBoxWidth; x += 3) {
      const price = minPrice + (x / viewBoxWidth) * priceRange;
      const exponent = -Math.pow(price - currentPrice, 2) / (2 * Math.pow(stdDev, 2));
      const probability = Math.exp(exponent);
      const y = viewBoxHeight - 50 - (probability * distributionHeight);
      bellPoints.push(`${x},${y}`);
    }

    const normalDistPath = `M ${bellPoints.join(' L ')} L ${viewBoxWidth},${viewBoxHeight - 50} L 0,${viewBoxHeight - 50} Z`;

    const currentPriceX = ((currentPrice - minPrice) / priceRange) * viewBoxWidth;

    const recentTrades = marketHistory.slice(-10).map((trade, i) => {
      const tradeX = ((trade.price - minPrice) / priceRange) * viewBoxWidth;
      const exponent = -Math.pow(trade.price - currentPrice, 2) / (2 * Math.pow(stdDev, 2));
      const probability = Math.exp(exponent);
      const tradeY = viewBoxHeight - 50 - (probability * distributionHeight);

      return {
        x: tradeX,
        y: tradeY,
        isPositive: trade.change >= 0,
        opacity: 0.3 + (i / 10) * 0.7
      };
    });

    return {
      normalDistPath,
      currentPriceX,
      minPrice,
      maxPrice,
      recentTrades,
      viewBoxWidth,
      viewBoxHeight
    };
  }, [marketHistory, currentPrice]);

  if (!marketData || !distributionData) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <div className={`w-4 h-4 border-2 border-t-transparent rounded-full animate-spin ${isLight ? 'border-primary' : 'border-accent'}`} />
          <div className={`font-mono text-[10px] tracking-widest uppercase ${isLight ? 'text-primary/40' : 'text-accent/40'}`}>
            Loading Distribution...
          </div>
        </div>
      </div>
    );
  }

  const { normalDistPath, currentPriceX, minPrice, maxPrice, recentTrades, viewBoxWidth, viewBoxHeight } = distributionData;

  return (
    <div className="w-full h-full relative group p-4">
      <svg
        className="w-full h-full opacity-90 transition-opacity duration-700 group-hover:opacity-100"
        viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter id="horizontal-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>

          <linearGradient id="horizontal-dist-gradient" x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor={accentHex} stopOpacity="0" />
            <stop offset="100%" stopColor={accentHex} stopOpacity="0.4" />
          </linearGradient>
        </defs>

        <line
          x1="0"
          y1={viewBoxHeight - 50}
          x2={viewBoxWidth}
          y2={viewBoxHeight - 50}
          stroke={gridColor}
          strokeWidth="1"
          opacity="0.2"
        />

        <line x1={viewBoxWidth * 0.25} y1="50" x2={viewBoxWidth * 0.25} y2={viewBoxHeight - 50}
              stroke={gridColor} strokeWidth="0.5" opacity="0.05" />
        <line x1={viewBoxWidth * 0.5} y1="50" x2={viewBoxWidth * 0.5} y2={viewBoxHeight - 50}
              stroke={gridColor} strokeWidth="0.5" opacity="0.05" />
        <line x1={viewBoxWidth * 0.75} y1="50" x2={viewBoxWidth * 0.75} y2={viewBoxHeight - 50}
              stroke={gridColor} strokeWidth="0.5" opacity="0.05" />

        <path
          d={normalDistPath}
          fill="url(#horizontal-dist-gradient)"
          stroke={accentHex}
          strokeWidth="2"
          filter="url(#horizontal-glow)"
          style={{ transition: 'all 0.3s ease-out' }}
        />

        <line
          x1={currentPriceX}
          y1="50"
          x2={currentPriceX}
          y2={viewBoxHeight - 50}
          stroke={accentHex}
          strokeWidth="2"
          strokeDasharray="6 4"
          opacity="0.8"
          style={{ transition: 'all 0.3s ease-out' }}
        >
          <animate
            attributeName="opacity"
            values="0.5;1;0.5"
            dur="2s"
            repeatCount="indefinite"
          />
        </line>

        <g transform={`translate(${currentPriceX}, 30)`} style={{ transition: 'all 0.3s ease-out' }}>
          <rect
            x="-50"
            y="-15"
            width="100"
            height="30"
            fill={accentHex}
            rx="4"
            filter="url(#horizontal-glow)"
          />
          <text
            x="0"
            y="4"
            fill={isLight ? 'white' : 'black'}
            fontSize="14"
            fontWeight="bold"
            fontFamily="monospace"
            textAnchor="middle"
          >
            ${currentPrice.toFixed(2)}
          </text>
          <text
            x="0"
            y="28"
            fill={priceChange >= 0 ? "#00ff88" : "#ff4d4d"}
            fontSize="10"
            fontFamily="monospace"
            textAnchor="middle"
            fontWeight="bold"
          >
            {priceChange >= 0 ? '▲' : '▼'} {Math.abs(priceChange).toFixed(2)}%
          </text>
        </g>

        {recentTrades.map((trade, i) => (
          <circle
            key={i}
            cx={trade.x}
            cy={trade.y}
            r="3"
            fill={trade.isPositive ? "#00ff88" : "#ff4d4d"}
            opacity={trade.opacity}
            style={{ transition: 'all 0.3s ease-out' }}
          >
            <animate
              attributeName="r"
              values="3;5;3"
              dur="2s"
              begin={`${i * 0.2}s`}
              repeatCount="indefinite"
            />
          </circle>
        ))}

        <text x="50" y={viewBoxHeight - 20} fill={labelColor} opacity="0.4" fontSize="11" fontFamily="monospace">
          ${minPrice.toFixed(2)}
        </text>
        <text x={viewBoxWidth - 100} y={viewBoxHeight - 20} fill={labelColor} opacity="0.4" fontSize="11" fontFamily="monospace">
          ${maxPrice.toFixed(2)}
        </text>
      </svg>
    </div>
  );
};

export default HorizontalNormalDistribution;
